// Turkic lore flavor. The mechanics are unchanged; this only renames the ages
// (and is imported by the UI) so progression reads as the sweep of Turkish
// history from the steppe to the stars. Working title only; the final brand name
// is decided before any commercial launch (trademark check pending).

import { getLang } from '@i18n/i18n';

// Age names, localized per active language. The historical ages keep their
// proper-noun dynasty forms (Göktürk stays Göktürk); the space ages are common
// nouns (Space, Planet, Star ...) that must localize, so a non-Turkish player
// no longer sees "Uzay Çağı" instead of "Space Age". Turkish is the source.
const AGE_NAMES: Record<string, string[]> = {
  tr: ['Bozkır Çağı', 'Göktürk Çağı', 'Selçuklu Çağı', 'Osmanlı Çağı', 'Cumhuriyet Çağı', 'Uzay Çağı', 'Gezegen Çağı', 'Yıldız Çağı', 'Galaksi Çağı', 'Kozmik Çağ', 'Gök Çağı', 'Ebedi Çağ'],
  en: ['Steppe Age', 'Göktürk Age', 'Seljuk Age', 'Ottoman Age', 'Republic Age', 'Space Age', 'Planet Age', 'Star Age', 'Galaxy Age', 'Cosmic Age', 'Sky Age', 'Eternal Age'],
  de: ['Steppenzeit', 'Göktürk-Zeit', 'Seldschuken-Zeit', 'Osmanische Zeit', 'Republik-Zeit', 'Weltraumzeit', 'Planetenzeit', 'Sternenzeit', 'Galaxienzeit', 'Kosmische Zeit', 'Himmelszeit', 'Ewige Zeit'],
  es: ['Edad de la Estepa', 'Edad Göktürk', 'Edad Selyúcida', 'Edad Otomana', 'Edad de la República', 'Edad Espacial', 'Edad Planetaria', 'Edad Estelar', 'Edad Galáctica', 'Edad Cósmica', 'Edad del Cielo', 'Edad Eterna'],
  fr: ['Âge de la Steppe', 'Âge Göktürk', 'Âge Seldjoukide', 'Âge Ottoman', 'Âge de la République', 'Âge Spatial', 'Âge Planétaire', 'Âge Stellaire', 'Âge Galactique', 'Âge Cosmique', 'Âge du Ciel', 'Âge Éternel'],
};

export function ageName(age: number): string {
  const list = AGE_NAMES[getLang()] ?? AGE_NAMES.en;
  return list[Math.min(Math.max(0, age), list.length - 1)] ?? `Age ${age + 1}`;
}

// One narrative "age dawn" beat per age, shown when the player reaches it. The
// arc threads the eternal blue Gök (sky) motif from the steppe to the stars and
// closes the loop at the Eternal age. Turkish master + English (the app's
// TR-master / EN-fallback convention; a full sentence, not a stat line).
const AGE_LORE: { tr: string; en: string }[] = [
  {
    tr: "Bozkırın bağrına bir avuç çadır kurdun; üstüne gerilen ilk ve tek dam, sonsuz mavi Gök oldu.",
    en: "You pitched a handful of tents in the heart of the steppe; the first and only roof stretched over you was the eternal blue Sky.",
  },
  {
    tr: "Kurt başlı sancak Gök'e kalktı; Gök de Türk adını ilk kez bir kağanın alnına yazdı.",
    en: "The wolf-headed standard rose toward the Sky, and for the first time the Sky wrote the name Türk upon a khagan's brow.",
  },
  {
    tr: "Malazgirt'ten Anadolu'nun bağrına aktın; her kervansarayda yol biraz daha uzadı, her medrese kubbesinde Gök biraz daha yaklaştı.",
    en: "You swept from Manzikert into the heart of Anatolia; in every caravanserai the road grew longer, in every madrasa dome the Sky drew a little nearer.",
  },
  {
    tr: "Topun surları döverken mimarının kubbesi Gök'e cevap verdi; bir çağı kapadın, bir çağı açtın.",
    en: "While your cannon battered the walls, your architect's dome answered the Sky; you closed one age and opened another.",
  },
  {
    tr: "Küllerinden bir millet doğurdun; Gök ilk kez bir hanedana değil bir halka baktı ve istikbal göklerde parladı.",
    en: "From the ashes you raised a nation; the Sky looked for the first time not upon a dynasty but upon a people, and the future shone in the skies.",
  },
  {
    tr: "Gök tavan olmaktan çıkıp kapı oldu; ateşten bir okun sırtında eşiğinden geçtin.",
    en: "The Sky ceased to be a ceiling and became a door; astride an arrow of fire, you crossed its threshold.",
  },
  {
    tr: "Yabancı göklerin altında çadırını yeniden kurdun; her gezegen, otağına katılan yeni bir bozkır oldu.",
    en: "Beneath alien skies you pitched your tent anew; every planet became a fresh steppe gathered into your great tent.",
  },
  {
    tr: "Yıldızları seyretme çağı bitti; Gök'ün sürüsünü güden çoban artık sensin.",
    en: "The age of watching stars is over; you are now the shepherd who drives the Sky's herd.",
  },
  {
    tr: "Samanyolu'nu göç yolun yaptın; yüz milyar ocağın ateşi tek otağının çevresinde saf tuttu.",
    en: "You made the Milky Way your road of migration; the fires of a hundred billion hearths stand in ranks around your single tent.",
  },
  {
    tr: "Evrenin ilk ışığını okudun ve gördün ki Gök, daha ilk andan beri bozkırın üstüne geriliymiş.",
    en: "You read the first light of the universe and saw that the Sky had been stretched over the steppe since the very first moment.",
  },
  {
    tr: "Son perde kalktı; Gök'e bakan göz de bakılan sonsuz mavilik de artık sensin.",
    en: "The last veil has lifted; the eye that beholds the Sky and the boundless blue it beholds are both you.",
  },
  {
    tr: "Çember kapandı; Gök yine mavi, bozkır yine uçsuz ve bir avuç çadırın üstünde ilk sabah seni yeniden bekliyor.",
    en: "The circle is closed; the Sky is blue once more, the steppe boundless once more, and above a handful of tents the first morning awaits you again.",
  },
];

/** The age-dawn narrative beat for a given age, in the active language (TR
 *  master, EN for every other locale, matching the news-copy convention). */
export function ageLore(age: number): string {
  const entry = AGE_LORE[Math.max(0, Math.min(age, AGE_LORE.length - 1))];
  if (!entry) return '';
  return getLang() === 'tr' ? entry.tr : entry.en;
}

// Settlement rank names as the settlement grows (indexed by tier). Localized so
// the rank label no longer shows Turkish in every locale. Otağ / Kağanlık keep
// their Turkic form (proper-noun rank); the middle tiers use local equivalents.
const TIER_NAMES: Record<string, string[]> = {
  tr: ['Otağ', 'Oba', 'Köy', 'Kasaba', 'Şehir', 'Kağanlık'],
  en: ['Otağ', 'Nomad Camp', 'Village', 'Town', 'City', 'Khaganate'],
  de: ['Otağ', 'Nomadenlager', 'Dorf', 'Kleinstadt', 'Stadt', 'Khaganat'],
  es: ['Otağ', 'Campamento Nómada', 'Aldea', 'Villa', 'Ciudad', 'Kaganato'],
  fr: ['Otağ', 'Campement Nomade', 'Village', 'Bourg', 'Ville', 'Khaganat'],
};

/** The settlement rank name for a given tier index, in the active language. */
export function tierName(tier: number): string {
  const list = TIER_NAMES[getLang()] ?? TIER_NAMES.en;
  return list[Math.max(0, Math.min(tier, list.length - 1))] ?? '';
}
